import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bot, Gamepad2, Loader2, CheckCircle, Calendar, Clock, Users, ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PublicLayout } from "@/components/PublicLayout";
import { workshopsAPI, bookingsAPI } from "@/lib/api";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

export default function SelectWorkshopPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    loadData();
  }, [childId]);

  const loadData = async () => {
    try {
      const verifyResponse = await workshopsAPI.list({ available_only: true });
      setWorkshops(verifyResponse.data);
      setChild({ id: childId });
    } catch (error) {
      toast.error("Erro ao carregar dados");
      navigate("/verificar");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredWorkshops = workshops.filter(w => w.available_slots > 0);

  const handleBooking = async () => {
    if (!selectedWorkshop || !childId) return;

    setIsBooking(true);

    try {
      const response = await bookingsAPI.create({
        child_id: childId,
        workshop_id: selectedWorkshop.id,
      });

      setBookingDetails(response.data);
      setBookingSuccess(true);
      toast.success("Agendamento confirmado com sucesso!");
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao realizar agendamento";
      toast.error(message);
    } finally {
      setIsBooking(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parse(dateStr, "yyyy-MM-dd", new Date()), "dd 'de' MMMM", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatWeekday = (dateStr) => {
    try {
      return format(parse(dateStr, "yyyy-MM-dd", new Date()), "EEEE", { locale: ptBR });
    } catch {
      return "";
    }
  };

  if (isLoading) {
    return (
      <PublicLayout showBack backTo="/verificar">
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PublicLayout>
    );
  }

  if (bookingSuccess && bookingDetails) {
    return (
      <PublicLayout>
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4">
          <Card className="w-full max-w-md glass-card shadow-2xl animate-scale-in">
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="font-nunito font-bold text-2xl mb-2">Agendamento Confirmado!</h2>
              <p className="text-muted-foreground mb-6">
                Sua presença foi confirmada com sucesso.
              </p>
              
              <div className="p-6 rounded-2xl bg-muted/50 mb-6 text-left">
                <div className="flex items-center gap-3 mb-4">
                  <Star className="w-8 h-8 text-amber-500" />
                  <div>
                    <p className="font-nunito font-bold text-lg">
                      Evento Completo (Robótica & Atleta Digital)
                    </p>
                    <p className="text-sm text-muted-foreground">{bookingDetails.child_name}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>{formatDate(bookingDetails.workshop_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>{bookingDetails.workshop_time}</span>
                  </div>
                </div>
              </div>

              {bookingDetails.whatsapp_notified && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-6">
                  Uma confirmação foi enviada para o WhatsApp do responsável.
                </p>
              )}

              <Button
                onClick={() => navigate("/")}
                className="w-full rounded-full btn-animate"
                data-testid="back-to-home-button"
              >
                Voltar ao Início
              </Button>
            </CardContent>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout showBack backTo="/verificar">
      <div className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium",
              !selectedWorkshop ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            )}>
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
              Escolher Data
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
            <div className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium",
              selectedWorkshop ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            )}>
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
              Confirmar
            </div>
          </div>

          {/* Step 1: Select Date/Time */}
          {!selectedWorkshop && (
            <div className="animate-fade-in">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-nunito font-bold text-2xl">
                  Escolha a Data do Evento
                </h2>
              </div>

              {filteredWorkshops.length === 0 ? (
                <Card className="border-dashed border-2">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Calendar className="w-12 h-12 mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground text-center">
                      Não há datas disponíveis para o evento no momento.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredWorkshops.map((workshop) => (
                    <Card
                      key={workshop.id}
                      className={cn(
                        "cursor-pointer border-2 transition-all hover:border-primary hover:shadow-lg"
                      )}
                      onClick={() => setSelectedWorkshop(workshop)}
                      data-testid={`select-workshop-${workshop.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/10">
                            <Star className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="font-nunito font-bold text-lg">
                              {formatDate(workshop.date)}
                            </p>
                            <p className="text-sm text-muted-foreground capitalize">
                              {formatWeekday(workshop.date)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{workshop.time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">
                              {workshop.available_slots} vagas
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Confirm Booking */}
          {selectedWorkshop && (
            <div className="animate-fade-in">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-nunito font-bold text-2xl">
                  Confirmar Agendamento
                </h2>
                <Button
                  variant="outline"
                  onClick={() => setSelectedWorkshop(null)}
                  className="rounded-full"
                  data-testid="back-to-dates-button"
                >
                  Voltar
                </Button>
              </div>

              <Card className="max-w-md mx-auto glass-card shadow-xl">
                <CardHeader className="text-center">
                  <CardTitle className="font-nunito">Resumo do Agendamento</CardTitle>
                  <CardDescription>Verifique os dados antes de confirmar</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 rounded-xl bg-muted/50">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/10">
                        <Star className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-nunito font-bold text-lg">Evento Completo</p>
                        <p className="text-sm text-muted-foreground">Robótica e Atleta Digital (1h 30m)</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>{formatDate(selectedWorkshop.date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedWorkshop.time}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleBooking}
                    disabled={isBooking}
                    className="w-full h-12 rounded-full btn-animate"
                    data-testid="confirm-booking-button"
                  >
                    {isBooking ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Confirmar Agendamento
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    Ao confirmar, você receberá uma notificação via WhatsApp.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
